import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { onMessage } from 'firebase/messaging';
import { auth, getMessagingInstance } from '../firebase';
import './PushToaster.css';

/**
 * Push messages are shown by the service worker only while the app is in the background. When the
 * app is open Firebase hands them to the page instead – so without this nothing would appear for
 * someone who is looking at the site. Shows them as small in-app toasts.
 */
export default function PushToaster() {
  const navigate = useNavigate();
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => setToasts((list) => list.filter((t) => t.id !== id)), []);

  useEffect(() => {
    let stopMessages = null;
    let cancelled = false;

    const stopAuth = onAuthStateChanged(auth, async (user) => {
      if (stopMessages) { stopMessages(); stopMessages = null; }
      if (!user || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
      const messaging = await getMessagingInstance();
      if (!messaging || cancelled) return;
      stopMessages = onMessage(messaging, (payload) => {
        const data = payload.data || {};
        const title = (payload.notification && payload.notification.title) || data.title;
        if (!title) return;
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setToasts((list) => [...list.slice(-2), {
          id,
          title,
          body: (payload.notification && payload.notification.body) || data.body || '',
          url: data.url || '/'
        }]);
        setTimeout(() => dismiss(id), 9000);
      });
    });

    return () => {
      cancelled = true;
      stopAuth();
      if (stopMessages) stopMessages();
    };
  }, [dismiss]);

  if (!toasts.length) return null;

  return (
    <div className="push-toaster" role="region" aria-label="Notifications" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className="push-toast">
          <button
            type="button"
            className="push-toast-main"
            onClick={() => {
              dismiss(t.id);
              if (t.url.startsWith('/')) navigate(t.url);
              else window.location.href = t.url;
            }}
          >
            <strong>{t.title}</strong>
            {t.body && <span>{t.body}</span>}
          </button>
          <button type="button" className="push-toast-close" aria-label="Dismiss" onClick={() => dismiss(t.id)}>✕</button>
        </div>
      ))}
    </div>
  );
}
